import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import moment from 'moment';
import {
  useListOrdersMutation,
  OrderDataResponse,
  useAppSelector,
  getWorkspaceId,
  dispatch,
  updateOrderStatus,
  useOrdersFiltersMutation,
  getAppliedFilters,
  FilterRoute,
  ListFilterKey,
  getAppState,
  APP_TYPE,
  getSearchParameter,
  getShowSearchWindow,
  updateExtraDetails,
} from '@zonofi/zono-money-store';
import {
  FIELD_OPS_ROUTES,
  SELLER_WEBAPP_ROUTES,
  SortDirection,
} from '../../constants';
import {
  useDebounceSearch,
  usePaginatedList,
  useUrlDateRange,
} from '../../hooks';
import { PNGS } from '../../assets';
import { searchCriterias, statusMap } from '../constants';
import { shallowEqual } from 'react-redux';
import { getOrderStatusText } from '../../helper';

export const useOrderList = ({ isCustomer }: { isCustomer: boolean }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { customerId: customerIdFromParam } = useParams();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerId');

  const appState = useAppSelector(getAppState);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const searchParameter = useAppSelector(getSearchParameter);
  const appliedFilters = useAppSelector(
    getAppliedFilters(FilterRoute.Order),
    shallowEqual
  );
  const showSearchWindow = useAppSelector(getShowSearchWindow);

  const [fetchOrdersApi, ordersApiResponse] = useListOrdersMutation();
  const [fetchOrdersFilter, ordersFilterResponse] = useOrdersFiltersMutation();

  const { appliedDateRange } = useUrlDateRange({
    route: isCustomer ? FilterRoute.CustomerOrder : FilterRoute.Order,
  });

  const [showSearch, setShowSearch] = useState(false);
  const [appliedStatus, setAppliedStatus] = useState<string[]>([]);
  const { debouncedSearchValue } = useDebounceSearch();

  const lastPath = useMemo(() => pathname.split('/').pop(), [pathname]);
  const updateStatusFilter = (status: string) => {
    setAppliedStatus(status ? [status] : []);
    if (
      ![
        SELLER_WEBAPP_ROUTES.ORDERS_LIST,
        FIELD_OPS_ROUTES.ORDERS_LIST,
        FIELD_OPS_ROUTES.CUSTOMER_PROFILE_ORDER,
      ].includes(pathname) &&
      status !== statusMap[lastPath as string]?.[0]
    ) {
      const baseRouteToNavigateTo =
        appState === APP_TYPE.FIELDS_OPS
          ? FIELD_OPS_ROUTES
          : SELLER_WEBAPP_ROUTES;
      const fullRouteToNavigateTo = customerIdFromParam
        ? undefined
        : baseRouteToNavigateTo.ORDERS_LIST;
      fullRouteToNavigateTo && navigate(fullRouteToNavigateTo);
    }
  };

  useEffect(() => {
    const status = statusMap[lastPath as string]?.[0];
    if (status) updateStatusFilter(statusMap[lastPath as string]?.[0]);
  }, [lastPath]);

  const onOrderItemPress = (order: OrderDataResponse) => {
    dispatch(updateOrderStatus(order.status));
    dispatch(
      updateExtraDetails({
        customerName:
          order?.customerName?.length > 0
            ? order?.customerName
            : order?.customerFirmName,
        customerId: order?.customerId,
      })
    );
    const baseRouteToNavigate =
      appState === APP_TYPE.FIELDS_OPS
        ? FIELD_OPS_ROUTES
        : SELLER_WEBAPP_ROUTES;
    const routeToNavigate = customerIdFromParam
      ? `${order.poFile.id}?orderId=${order?.id}&customerId=${customerIdFromParam}`
      : `${baseRouteToNavigate.ORDER_DETAILS}/${order.poFile?.id}?orderId=${order?.id}&customerId=${order?.customerId}`;
    navigate(routeToNavigate);
  };

  const emptyStateMap: Record<string, { image: string; message: string }> = {
    submitted: {
      image: PNGS.NoOrdersFound,
      message: 'No Orders Found',
    },
    draft: {
      image: PNGS.NoOrdersFound,
      message: 'No Draft Orders Found',
    },
    accepted: {
      image: PNGS.NoOrdersFound,
      message: 'No orders to fulfil',
    },
    orders: {
      image: PNGS.NoOrdersFound,
      message: 'No orders found',
    },
    order: {
      image: PNGS.NoOrdersFound,
      message: 'No orders found',
    },
  };

  const memoizedOrderData = useMemo(() => {
    return ordersApiResponse?.data?.order ?? [];
  }, [ordersApiResponse?.data?.order]);

  const isOrdersApiLoading = useMemo(() => {
    return ordersApiResponse?.isLoading || ordersFilterResponse?.isLoading;
  }, [ordersApiResponse, ordersFilterResponse]);

  const statusFilter = useMemo(() => {
    const status = statusMap[lastPath as string] || [];
    if (status.length !== 0) return status;
    return appliedStatus.length > 0 ? appliedStatus : [];
  }, [appliedStatus, lastPath]);

  const statusFilters = useMemo(() => {
    const filters =
      ordersFilterResponse?.data?.status
        ?.slice()
        .sort((a, b) => b.statusCount - a.statusCount)
        .map(({ status, statusCount }) => ({
          id: status,
          value: `${getOrderStatusText(status)} (${statusCount})`,
          selected: appliedStatus.includes(status),
        })) || [];
    filters.unshift({
      id: '',
      value: 'All',
      selected: appliedStatus.length === 0,
    });
    return filters;
  }, [lastPath, ordersFilterResponse?.data?.status, appliedStatus]);

  const cfaIds = appliedFilters.listFilter?.[ListFilterKey.Cfa] || [];
  const tagIds =
    appliedFilters.listFilter?.[ListFilterKey.OrderTags]?.map(Number) || [];

  const fetchOrders = useCallback(
    ({ pageNo, pageSize }: { pageNo: number; pageSize: number }) => {
      const { parameterId, updateSearchValue = '' } = searchParameter!;
      const scopedCustomerId =
        customerId ||
        customerIdFromParam ||
        (parameterId === 'customerId'
          ? (updateSearchValue as string)
          : undefined);

      fetchOrdersApi({
        params: {
          workspaceId: sellerWorkspaceId,
          customerId: undefined,
          searchKey: debouncedSearchValue ?? undefined,
          startDate: appliedDateRange?.startDate?.toString() ?? undefined,
          endDate: appliedDateRange?.endDate.toString() ?? undefined,
        },
        body: {
          workspaceId: sellerWorkspaceId,
          customerId: '',
          skip: pageNo,
          sortBy: appliedFilters?.sortOptions?.field ?? 'orderPlacedAt',
          sortDirection:
            appliedFilters?.sortOptions?.direction ?? SortDirection.DESCENDING,
          includeInvoice: true,
          startDate: moment(appliedDateRange?.startDate)
            .startOf('day')
            .format(),
          endDate: moment(appliedDateRange?.endDate)
            .add(1, 'days')
            .startOf('day')
            .format(),
          ...(parameterId.length > 0 &&
            parameterId === 'searchKeyword' && {
              sarchKeyword: searchParameter?.updateSearchValue,
            }),
          filterModel: {
            status: statusFilter,
            ...(parameterId.length > 0 &&
            parameterId !== 'customerId' &&
            parameterId !== 'searchKeyword' &&
            updateSearchValue &&
            updateSearchValue?.length > 0
              ? { [parameterId]: searchParameter?.updateSearchValue }
              : {}),
            ...(scopedCustomerId && {
              customerIds: [scopedCustomerId],
            }),
            cfaIds,
            tagIds,
          },
          pageNo,
          pageSize,
        },
      });
    },
    [
      appliedDateRange,
      statusFilter,
      searchParameter,
      customerId,
      customerIdFromParam,
    ]
  );

  const {
    data: ordersData,
    pageNo,
    lastItemRef,
    isPageLoading,
    onScroll,
    refresh,
    isEndOfList,
  } = usePaginatedList<OrderDataResponse>({
    fetchDataApiCall: fetchOrders,
    apiResponseData: memoizedOrderData,
    isLoading: ordersApiResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [
      appliedDateRange?.startDate.toString(),
      appliedDateRange?.endDate.toString(),
      statusFilter,
      searchParameter?.updateSearchValue,
      debouncedSearchValue,
      appliedFilters,
    ],
    isError: ordersApiResponse.isError,
    paginationMeta: {
      startRecord: ordersApiResponse?.data?.startRecord || 0,
      endRecord: ordersApiResponse?.data?.endRecord || 0,
      totalCount: ordersApiResponse?.data?.totalRecords || 0,
    },
  });

  useEffect(() => {
    const scopedCustomerId = customerId || customerIdFromParam;

    fetchOrdersFilter({
      workspaceId: sellerWorkspaceId,
      ...(scopedCustomerId && {
        filterModel: {
          customerIds: [scopedCustomerId],
        },
      }),
      customerId: '',
      startDate: moment(appliedDateRange?.startDate).startOf('day').format(),
      endDate: moment(appliedDateRange?.endDate)
        .add(1, 'days')
        .startOf('day')
        .format(),
    });
  }, [
    JSON.stringify(appliedDateRange?.startDate),
    JSON.stringify(appliedDateRange?.endDate),
    lastPath,
    customerId,
    customerIdFromParam,
  ]);

  return {
    ordersData,
    pageNo,
    lastItemRef,
    isPageLoading,
    lastPath,
    onOrderItemPress,
    emptyStateMap,
    statusFilters,
    updateStatusFilter,
    onScroll,
    searchCriterias,
    searchParameter,
    showSearchWindow,
    showSearch,
    setShowSearch,
    isOrdersApiLoading,
    isLoading:
      pageNo === 1 &&
      (ordersApiResponse?.isUninitialized || ordersApiResponse?.isLoading),
    isEndOfList,
    appliedDateRange,
    refreshOrderList: refresh,
  };
};
